import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { BookOpen, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";

export default function Journaling() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) API.get("/journals").then(({ data }) => setEntries(data || [])).catch(() => {}); }, [user]);

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) return;
    setLoading(true);
    try {
      const now = new Date();
      await API.post("/journals", { title: formData.title.trim(), content: formData.content.trim(), entry_date: now.toISOString().split("T")[0], entry_time: now.toTimeString().split(" ")[0] });
      setFormData({ title: "", content: "" }); setIsCreating(false);
      const { data } = await API.get("/journals"); setEntries(data || []);
    } catch { } finally { setLoading(false); }
  };

  const formatDateTime = (date, time) => {
    const d = new Date(date); const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return time ? `${dateStr} at ${time.slice(0, 5)}` : dateStr;
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 animate-fade-in" data-testid="journaling-page">
      <PageHeader title="My Journal" subtitle="Reflect, write, and track your thoughts" />
      {isCreating ? (
        <div className="bg-card border border-border rounded-xl p-5 mb-6 space-y-4 animate-fade-in">
          <input type="text" placeholder="Entry Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring" data-testid="journal-title-input" />
          <textarea placeholder="Write your thoughts here..." value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring h-64 resize-none" data-testid="journal-content-input" />
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={loading || !formData.title.trim()} data-testid="save-journal-button">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "Save Entry"}</Button>
            <Button variant="outline" onClick={() => { setIsCreating(false); setFormData({ title: "", content: "" }); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setIsCreating(true)} className="w-full mb-6 gap-2 rounded-full" data-testid="new-journal-button"><Plus className="w-4 h-4" />New Entry</Button>
      )}
      {entries.length === 0 ? <EmptyState icon={BookOpen} title="No journal entries yet" description="Start journaling to capture your thoughts" /> : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <button key={entry.id} onClick={() => setSelectedEntry(selectedEntry?.id === entry.id ? null : entry)}
              className="w-full text-left bg-card border border-border rounded-xl p-4 hover:bg-accent/50 transition-colors" data-testid="journal-entry">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">{entry.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{formatDateTime(entry.entry_date, entry.entry_time)}</p>
                </div>
                <span className="text-muted-foreground text-sm">{selectedEntry?.id === entry.id ? "v" : ">"}</span>
              </div>
              {selectedEntry?.id === entry.id && (
                <div className="mt-3 pt-3 border-t border-border animate-fade-in"><p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{entry.content}</p></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
